import {
	getSubscription,
	getDareKeyUploadUrl,
	getDareKeyDownloadUrl,
} from '../../Utils/getBaseUrl';
import axios from 'axios';
import * as ActionTypes from '../../constants/actionTypes';

export const failedDL = (data) => ({
	type: ActionTypes.GET_DEVICE_LIST_FAILED,
	payload: data,
});

// Download DareKey file
export const downloadDareKeyFile = (serialNumber) => (dispatch, getState) => {
	const state = getState();
	const token = state.userAccount.stateValue;
	const downloadUrl = getDareKeyDownloadUrl();
	const esbSubscriptionKey = getSubscription();

	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
			'Content-Type': `application/json`,
			'Cache-Control': 'no-cache',
			Pragma: 'no-cache',
			Expires: '0',
			'EsbApi-Subscription-Key': esbSubscriptionKey,
		},
	};

	// DareKeyDownloadRequestModel
	const payload = {
		SerialNumber: serialNumber,
	};

	const errorMessage = {
		statusCode: '',
		message: '',
	};

	return axios
		.create(config)
		.post(downloadUrl, payload)
		.then((res) => {
			errorMessage.statusCode = res.status;
			if (res.status === 200) {
				// DareKeyFileDownloadResponseModel - API response has structure: {state, data, error}
				const responseData = res.data;
				
				// Check if response is successful and has data
				if (responseData && responseData.state && responseData.data && responseData.data.fileContent) {
					const fileData = responseData.data;
					
					// Decode Base64 content
					const byteCharacters = atob(fileData.fileContent);
					const byteNumbers = new Array(byteCharacters.length);
					for (let i = 0; i < byteCharacters.length; i++) {
						byteNumbers[i] = byteCharacters.charCodeAt(i);
					}
					const byteArray = new Uint8Array(byteNumbers);
					const blob = new Blob([byteArray], { type: fileData.contentType || 'application/octet-stream' });
					
					// Create download URL
					const url = window.URL.createObjectURL(blob);
					const filename = fileData.fileName || `MBID_Device_${serialNumber}.enc`;
					
					// Trigger download
					const link = document.createElement('a');
					link.href = url;
					link.setAttribute('download', filename);
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
					window.URL.revokeObjectURL(url);

					return {
						status: true,
						message: 'File downloaded successfully',
						data: fileData,
					};
				} else {
					// Check if API returned an error
					if (responseData && !responseData.state && responseData.error) {
						errorMessage.message = responseData.error.message || 'Unknown error occurred';
					} else {
						errorMessage.message = 'No file content received from server';
					}
					dispatch(failedDL(errorMessage));
					return {
						status: false,
						statusCode: 200,
						message: errorMessage.message,
					};
				}
			} else if (res.status === 404) {
				errorMessage.message = 'File not found for the provided serial number';
				dispatch(failedDL(errorMessage));
				return {
					status: false,
					statusCode: 404,
					message: errorMessage.message,
				};
			} else {
				errorMessage.message = 'Unable to download file. Please try again.';
				dispatch(failedDL(errorMessage));
				return {
					status: false,
					statusCode: res.status,
					message: errorMessage.message,
				};
			}
		})
		.catch((error) => {
			if (error.response) {
				const status = error.response.status;
				errorMessage.statusCode = status;
				
				if (status === 404) {
					errorMessage.message = 'File not found for the provided serial number';
				} else if (status === 401) {
					errorMessage.message = 'Access is denied due to the session timeout. Please login again.';
				} else if (status === 500) {
					errorMessage.message = 'Internal server error. Please try again.';
				} else {
					errorMessage.message = 'Something went wrong during download';
				}
				
				dispatch(failedDL(errorMessage));
				return {
					status: false,
					statusCode: status,
					message: errorMessage.message,
				};
			} else if (error.request) {
				errorMessage.statusCode = 401;
				errorMessage.message = 'Your request could not be processed due to Network Interruption. Please login again.';
				dispatch(failedDL(errorMessage));
				return {
					status: false,
					statusCode: 401,
					message: errorMessage.message,
				};
			} else {
				errorMessage.statusCode = 500;
				errorMessage.message = error.message;
				dispatch(failedDL(errorMessage));
				return {
					status: false,
					statusCode: 500,
					message: errorMessage.message,
				};
			}
		});
};



// Upload DareKey files with progress tracking
export const uploadDareKeyFiles = (files, onProgress) => (dispatch, getState) => {
	const state = getState();
	const token = state.userAccount.stateValue;
	const uploadUrl = getDareKeyUploadUrl();
	const esbSubscriptionKey = getSubscription();

	// Create FormData for multipart upload (IFormFileCollection)
	const formData = new FormData();
	
	// Add each file to the FormData
	files.forEach((file, index) => {
		formData.append('files', file); // 'files' is the parameter name for IFormFileCollection
	});

	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
			'EsbApi-Subscription-Key': esbSubscriptionKey,
			// Don't set Content-Type, let browser set it automatically for multipart/form-data
		},
		onUploadProgress: (progressEvent) => {
			const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
			if (onProgress) {
				// Report progress for all files (since we're doing bulk upload)
				files.forEach((file, index) => {
					onProgress(index, progress, file.name);
				});
			}
		},
	};

	return axios
		.create(config)
		.post(uploadUrl, formData)
		.then((res) => {
			if (res.status === 200) {
				// DareKeyFileUploadResponseModel
				const responseData = res.data;
				
				// Handle ApplicationResponse wrapper if present
				const actualData = responseData.data || responseData.Data || responseData;
				
				// Convert backend response to frontend format
				const results = [];
				
				// Process upload results (successful uploads)
				if (actualData.uploadResults && actualData.uploadResults.length > 0) {
					actualData.uploadResults.forEach(uploadResult => {
						results.push({
							success: uploadResult.success,
							filename: uploadResult.fileName,
							serialNumber: uploadResult.serialNumber,
							error: uploadResult.errorMessage,
							status: uploadResult.status,
							progress: uploadResult.progressPercentage || 100,
							response: uploadResult
						});
					});
				}
				
				// Process validation results (including failed validations)
				if (actualData.validationResults && actualData.validationResults.length > 0) {
					actualData.validationResults.forEach(validationResult => {
						if (!validationResult.isValid) {
							// Find if this file already has an upload result
							const existingResult = results.find(r => r.filename === validationResult.fileName);
							if (!existingResult) {
								results.push({
									success: false,
									filename: validationResult.fileName,
									serialNumber: validationResult.serialNumber,
									error: validationResult.validationErrors.join(', '),
									status: 'Invalid',
									progress: 0,
									response: validationResult
								});
							}
						}
					});
				}
				
				// If no results but we have files, create default success results
				if (results.length === 0 && files.length > 0) {
					files.forEach(file => {
						results.push({
							success: true,
							filename: file.name,
							serialNumber: '',
							error: null,
							status: 2, // Completed
							progress: 100,
							response: null
						});
					});
				}
				
				return {
					status: true,
					results: results,
					summary: {
						total: files.length,
						successful: actualData.successfulUploads || results.filter(r => r.success).length,
						failed: actualData.failedUploads || results.filter(r => !r.success && r.status !== 'Invalid').length,
						invalid: actualData.invalidFiles || results.filter(r => r.status === 'Invalid').length
					},
					data: actualData
				};
			} else {
				return {
					status: false,
					results: files.map(file => ({
						success: false,
						filename: file.name,
						error: 'Upload failed with status: ' + res.status
					})),
					statusCode: res.status,
					message: 'Upload failed'
				};
			}
		})
		.catch((error) => {
			let errorMessage = 'Upload failed';
			let statusCode = 500;
			
			if (error.response) {
				statusCode = error.response.status;
				
				// Handle ApplicationResponse error wrapper
				if (error.response.data) {
					if (error.response.data.Error && error.response.data.Error.Message) {
						errorMessage = error.response.data.Error.Message;
					} else if (error.response.data.message) {
						errorMessage = error.response.data.message;
					}
				}
				
				if (statusCode === 401) {
					errorMessage = 'Access denied. Please login again.';
				} else if (statusCode === 400) {
					// Keep the specific error message from backend
					if (!error.response.data || !error.response.data.Error) {
						errorMessage = 'Bad request. Please check your files and try again.';
					}
				} else if (statusCode === 500) {
					errorMessage = 'Internal server error';
				}
			} else if (error.request) {
				statusCode = 401;
				errorMessage = 'Network error. Please check your connection and login again.';
			}
			
			// Dispatch error for Redux store
			const errorObj = {
				statusCode: statusCode,
				message: errorMessage,
			};
			dispatch(failedDL(errorObj));
			
			return {
				status: false,
				results: files.map(file => ({
					success: false,
					filename: file.name,
					error: errorMessage
				})),
				statusCode: statusCode,
				message: errorMessage
			};
		});
};